import { FormState } from "formstate";
import { fromPairs, isEmpty, omit, size } from "lodash";
import { useEffect, useState } from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { useStore } from "../../../../models";
import { LoadingState } from "../../../HOC/withPreloading/types";
import { useSettingsListFilters } from "../../settingsForms/filters/useListFilter/useListFilter";
import {
  ListHook,
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params,
  EditedItemsState,
  SelectedItemsState
} from "./types";

export const useSettingsList = <
  ExistingItem extends FormState<any>,
  NewItem extends FormState<any>,
  ApiItem extends { id: string }
>(
  params: Params<ExistingItem, NewItem, ApiItem>
): ListHook<ExistingItem, NewItem> => {
  const {
    apiGetItems,
    apiCreateItem,
    apiUpdateItem,
    apiDeleteItem,
    mapItemToForm,
    createNewForm,
    copyExistingForm,
    saveErrorId,
    onInitializeResult,

    deleteErrorId,
    onSubmitCallback,
    onDiscardCallback,
    sortBy,

    batchUpdateItems,

    validateSubmit,
    validateNew,

    overridenSaveNewItem
  } = params;

  const { uiStore } = useStore();
  const [editedItems, setEditedItems] = useState<EditedItemsState<ExistingItem>>({});
  const [newItem, setNewItem] = useState<NewItem>();
  const [items, setItems] = useState<ExistingItem[]>([]);
  const [initialItems, setInitialItems] = useState<ExistingItem[]>([]);

  const clearEditedItems = () => {
    setEditedItems({});
  };

  const setItemEdited = (item: ExistingItem) => {
    setEditedItems((current) => {
      return { ...current, [item.$.id.$]: item };
    });
  };

  const loadExistingItems = async () => {
    try {
      const result = await apiGetItems();
      const sorted = sortBy ? result.sort(sortBy) : result;
      const sortedForms = sorted.map(mapItemToForm);

      setItems(sortedForms);
      setInitialItems(sortedForms.map(copyExistingForm));

      clearEditedItems();
      onInitializeResult(LoadingState.Loaded);
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("settings.error.couldntLoadList"));
      logger.error({ message: "Couldn't load items", error: e });
      onInitializeResult(LoadingState.Error);
    }
  };

  useEffect(() => {
    loadExistingItems();
  }, []);

  const handleCreateNewClick = () => {
    setNewItem(createNewForm());
  };

  const discardNewItem = () => {
    setNewItem(undefined);
  };

  const saveNewItem =
    overridenSaveNewItem ||
    (async () => {
      if (!newItem || !apiCreateItem) {
        return;
      }

      const validate = await newItem.validate();
      if (validate.hasError) {
        uiStore.addErrorToast(intl.formatMessage(saveErrorId));
        return;
      }

      if (validateNew && !validateNew(newItem, items)) {
        return;
      }

      try {
        const item = await apiCreateItem(newItem);

        setItems([mapItemToForm(item), ...items]);
        setInitialItems([mapItemToForm(item), ...initialItems]);

        discardNewItem();
      } catch (e) {
        logger.error({ message: "Couldn't create new item", error: e });
        uiStore.addErrorToast(intl.formatMessage("error.something"));
      }
    });

  const updateExistingItem = async (item: ExistingItem) => {
    if (!apiUpdateItem) {
      logger.error("Cannot update existing item, no apiUpdateItem provided");
      return;
    }

    const validate = await item.validate();
    if (validate.hasError) {
      uiStore.addErrorToast(intl.formatMessage(saveErrorId));
      return;
    }

    try {
      await apiUpdateItem(item);
    } catch (e) {
      logger.error({ message: "Couldn't update existing item", error: e });
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const uiRemoveExistingItem = (id: string) => {
    setItems((current) => current.filter((item) => item.$.id.$ !== id));
    setInitialItems((current) => current.filter((item) => item.$.id.$ !== id));
  };

  const uiRemoveExistingItems = (ids: string[]) => {
    setItems((current) => current.filter((item) => !ids.includes(item.$.id.$)));
    setInitialItems((current) => current.filter((item) => !ids.includes(item.$.id.$)));
  };

  const onDiscardAll = () => {
    setItems(initialItems.map(copyExistingForm));
    clearEditedItems();

    if (onDiscardCallback) {
      onDiscardCallback();
    }
  };

  const onSubmitAll = async () => {
    if (!validateSubmit || validateSubmit(items)) {
      try {
        const itemsToUpdate = items.filter((item) => editedItems[item.$.id.$]);
        const update = batchUpdateItems
          ? batchUpdateItems(itemsToUpdate)
          : Promise.all(itemsToUpdate.map(updateExistingItem));

        await update;
        if (onSubmitCallback) {
          onSubmitCallback(items);
        }
        setInitialItems(items.map(copyExistingForm));
        clearEditedItems();
      } catch (e) {
        logger.error({ message: "Couldn't submit items", error: e });
        uiStore.addGenericErrorToast();
      }
    }
  };

  const updateItem = (item: ExistingItem) => {
    setInitialItems(
      initialItems.map((i) => {
        return i.$.id.$ === item.$.id.$ ? item : i;
      })
    );
    setItems(
      items.map((i) => {
        return i.$.id.$ === item.$.id.$ ? item : i;
      })
    );
  };

  return {
    initialItems,
    editedItems,
    clearEditedItems,
    setItemEdited,
    newItem,
    setNewItem,
    items,
    setItems,
    copyExistingForm,
    saveNewItem,
    handleCreateNewClick,
    discardNewItem,
    onDiscardAll,
    onSubmitAll,
    uiRemoveExistingItem,
    uiRemoveExistingItems,
    apiDeleteItem,
    deleteErrorId,
    updateItem,
    updateExistingItem
  };
};

export const useSettingsListEditable = <
  ExistingItem extends FormState<any>,
  NewItem extends FormState<any>,
  ApiItem extends { id: string }
>(
  params: Params<ExistingItem, NewItem, ApiItem>
): ListHookEditable<ExistingItem, NewItem> => {
  const { uiStore } = useStore();
  const [currentlyEditedItem, setCurrentlyEditedItem] = useState<ExistingItem>();
  const base = useSettingsList(params);

  const onExistingEditClick = (form: ExistingItem) => {
    if (currentlyEditedItem) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    if (!isEmpty(base.editedItems)) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    setCurrentlyEditedItem(base.copyExistingForm(form));
  };

  const saveEditedItemChangesLocally = async () => {
    if (!currentlyEditedItem) {
      return;
    }

    base.setItems(
      base.items.map((i) => {
        return currentlyEditedItem.$.id.$ === i.$.id.$ ? currentlyEditedItem : i;
      })
    );
    base.setItemEdited(currentlyEditedItem);
    discardEditedItem();
  };

  const discardEditedItem = () => {
    setCurrentlyEditedItem(undefined);
  };

  return {
    ...base,
    currentlyEditedItem,
    setCurrentlyEditedItem,
    discardEditedItem,
    saveEditedItemChangesLocally,
    onExistingEditClick
  };
};

/**
 * Decorator for useSettingsList hook (or useSettingsListEditable), MUST be used within SettingsListFilterProvider
 * takes care of filtering items on active filter changes
 */
export const withFilters = <ExistingItem, NewItem, T extends ListHook<ExistingItem, NewItem>>(
  hook: T
): ListHookWithFilters<ExistingItem, NewItem, T> => {
  const [filteredItems, setFilteredItems] = useState<typeof hook["items"]>([]);

  const { activeFilters } = useSettingsListFilters<typeof hook["items"][0]>();

  useEffect(() => {
    if (isEmpty(activeFilters)) {
      setFilteredItems(hook.items);
    } else {
      const itemPredicates = Object.values(activeFilters).map((af) => af.predicate);
      const itemPredicate = (item: ExistingItem): boolean => !itemPredicates.find((p) => !p(item));
      setFilteredItems(hook.items.filter(itemPredicate));
    }
  }, [hook.items, activeFilters]);

  return {
    ...hook,
    filteredItems,
    activeFiltersNumber: size(activeFilters)
  };
};

/**
 * Decorator for useSettingsList hook (or useSettingsListEditable), provides tracking and operations for selected items
 */
export const withSelectables = <
  ExistingItem extends FormState<any>,
  NewItem,
  T extends ListHook<ExistingItem, NewItem>
>(
  hook: T
): ListHookWithSelectables<ExistingItem, NewItem, T> => {
  const { uiStore } = useStore();
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState<ExistingItem>>({});

  const getAllSelected = (): ExistingItem[] => {
    return hook.items.filter((i) => !!selectedItems[i.$.id.$]);
  };

  const forEachSelected = (fn: (item: ExistingItem) => void): void => {
    getAllSelected().forEach((item) => fn(item));
  };

  const forAllSelected = (fn: (item: ExistingItem) => boolean): boolean => {
    return !isEmpty(selectedItems) && !Object.values(selectedItems).find((i) => !fn(i));
  };

  const setItemSelected = (item: ExistingItem, selected: boolean) => {
    if (selected) {
      setSelectedItems((current) => {
        return { ...current, [item.$.id.$]: item };
      });
    } else {
      setSelectedItems((current) => {
        const { [item.$.id.$]: v, ...rest } = current;
        return rest;
      });
    }
  };

  const setItemsSelected = (items: ExistingItem[], selected: boolean) => {
    if (selected) {
      const itemsToSelect = fromPairs(items.map((item) => [item.$.id.$, item]));
      setSelectedItems(itemsToSelect);
    } else {
      setSelectedItems(
        omit(
          selectedItems,
          items.map((i) => i.$.id.$)
        )
      );
    }
  };

  const setItemsSelectedOnPredicate = (
    items: ExistingItem[],
    selected: boolean,
    predicate: (item: ExistingItem) => boolean
  ) => {
    if (selected) {
      const itemsToSelect = fromPairs(items.filter(predicate).map((item) => [item.$.id.$, item]));
      setSelectedItems(itemsToSelect);
    } else {
      setSelectedItems({});
    }
  };

  const deselectAll = () => {
    setSelectedItems({});
  };

  const deleteAllSelected = async () => {
    const ids = Object.values(selectedItems).map((i) => i.$.id.$);
    const updates = ids.map(hook.apiDeleteItem);

    try {
      deselectAll();
      await Promise.all(updates);

      hook.uiRemoveExistingItems(ids);
    } catch (e) {
      logger.error({ message: "Couldn't delete items", error: e });
      if (hook.deleteErrorId) {
        uiStore.addErrorToast(intl.formatMessage(hook.deleteErrorId));
      } else {
        uiStore.addGenericErrorToast();
      }
    }
  };

  return {
    ...hook,
    getAllSelected,
    selectedItems,
    forEachSelected,
    forAllSelected,
    setItemSelected,
    setItemsSelected,
    setItemsSelectedOnPredicate,
    deleteAllSelected,
    deselectAll
  };
};
